import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, StarIcon, BuildingStorefrontIcon, XMarkIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import CampSearchInput from '../components/CampSearchInput';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useSession } from '@supabase/auth-helpers-react';
import { hasClaimedCamp } from '../lib/authHelpers';
import AlreadyClaimedModal from '../components/AlreadyClaimedModal';
import toast from 'react-hot-toast';
import { track } from '../lib/track';
import { trackEvent } from '../lib/analytics';

export default function MyCampTeaserPage() {
  const session = useSession();
  const navigate = useNavigate();
  const [selectedCamp, setSelectedCamp] = useState<any>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [showAlreadyClaimedModal, setShowAlreadyClaimedModal] = useState(false);

  // Check for camp in session storage on component mount and claim it if user is logged in
  useEffect(() => {
    const loadAndClaimCamp = async () => {
      const campSlug = sessionStorage.getItem('pending_claim_camp_slug');
      const campId = sessionStorage.getItem('pending_claim_camp_id');
      
      if (campSlug && campId) {
        // Fetch camp details
        try {
          const { data, error } = await supabase
            .from('camps')
            .select('id, name, slug, city, state, claimed, owner_id, plan_id')
            .eq('id', campId)
            .single();
          
          if (error) throw error;
          
          if (data) {
            // If user is logged in and camp is not claimed, claim it now (free claim)
            if (session?.user?.id && !data.claimed) {
              const { error: claimError } = await supabase
                .from('camps')
                .update({
                  claimed: true,
                  owner_id: session.user.id,
                  claimed_at: new Date().toISOString()
                  // Note: plan_id is set later when they pay for editing access
                })
                .eq('id', campId);
              
              if (claimError) {
                console.error('Error claiming camp:', claimError);
                toast.error('Failed to claim camp. Please try again.', { duration: 5000 });
                setShowSearch(true);
                return;
              }
              
              console.log('Camp claimed successfully (free claim)');
              // Refresh camp data
              const { data: updatedCamp } = await supabase
                .from('camps')
                .select('id, name, slug, city, state, claimed, owner_id, plan_id')
                .eq('id', campId)
                .single();
              
              if (updatedCamp) {
                setSelectedCamp(updatedCamp);
              }
            } else if (data.claimed) {
              // Camp already claimed, just set the state
              setSelectedCamp(data);
            } else {
              // Camp not claimed and user not logged in - show search
              setShowSearch(true);
            }
          }
        } catch (error) {
          console.error('Error fetching camp details:', error);
          setShowSearch(true);
        }
      } else {
        // No camp in session storage - check if user has a claimed camp
        if (session?.user?.id) {
          const { data: claimedCamps } = await supabase
            .from('camps')
            .select('id, name, slug, city, state, claimed, owner_id, plan_id')
            .eq('owner_id', session.user.id)
            .eq('claimed', true)
            .limit(1);
          
          if (claimedCamps && claimedCamps.length > 0) {
            setSelectedCamp(claimedCamps[0]);
          } else {
            setShowSearch(true);
          }
        } else {
          setShowSearch(true);
        }
      }
    };

    loadAndClaimCamp();
  }, [session]);

  // Page view
  useEffect(() => {
    trackEvent('mycamp_teaser_page_view', {
      is_authenticated: !!session,
      has_selected_camp: !!selectedCamp,
    });
    track('page_view', {
      persona: 'camp_owner',
      page: 'mycamp_teaser',
      is_authenticated: !!session,
    });
  }, []); // intentional: once on mount

  // Clear selected camp, unclaim it, and show search
  const clearSelectedCamp = async () => {
    // If camp is claimed by current user, unclaim it
    if (selectedCamp && selectedCamp.claimed && selectedCamp.owner_id === session?.user?.id) {
      try {
        console.log('Unclaiming camp:', {
          campId: selectedCamp.id,
          currentOwnerId: selectedCamp.owner_id,
          userId: session?.user?.id
        });
        
        const { data, error: unclaimError } = await supabase
          .from('camps')
          .update({
            claimed: false,
            owner_id: null,
            plan_id: null,
            claimed_at: null
          })
          .eq('id', selectedCamp.id)
          .select();
        
        if (unclaimError) {
          console.error('Error unclaiming camp:', {
            error: unclaimError,
            code: unclaimError.code,
            message: unclaimError.message,
            details: unclaimError.details,
            hint: unclaimError.hint
          });
          toast.error(`Failed to remove camp: ${unclaimError.message || 'Please try again.'}`, { duration: 5000 });
          return;
        }
        
        console.log('Camp unclaimed successfully:', data);
        toast.success('Camp removed from your account', { duration: 3000 });
      } catch (error: any) {
        console.error('Error unclaiming camp:', error);
        toast.error(`Failed to remove camp: ${error?.message || 'Please try again.'}`, { duration: 5000 });
        return;
      }
    }
    
    sessionStorage.removeItem('pending_claim_camp_slug');
    sessionStorage.removeItem('pending_claim_camp_id');
    setSelectedCamp(null);
    setShowSearch(true);
  };

  // Handle camp selection from search
  const handleCampSelect = async (camp: any) => {
    track('cta_clicked', {
      persona: 'camp_owner',
      page: 'mycamp_teaser',
      cta_location: 'search_camp_selected',
      cta_text: 'Camp selected from search',
      camp_id: camp?.id,
      camp_slug: camp?.slug,
    });
    trackEvent('mycamp_teaser_camp_selected', {
      camp_id: camp?.id,
      camp_slug: camp?.slug,
      is_authenticated: !!session,
    });

    // Check if user already has a claimed camp
    if (session?.user?.id) {
      const alreadyHasCamp = await hasClaimedCamp(session.user.id);
      if (alreadyHasCamp) {
        trackEvent('mycamp_teaser_already_claimed_modal_shown', {
          camp_id: camp?.id,
          reason: 'user_has_claimed_camp',
        });
        track('cta_clicked', {
          persona: 'camp_owner',
          page: 'mycamp_teaser',
          cta_location: 'search_blocked_already_claimed',
          camp_id: camp?.id,
        });
        setShowAlreadyClaimedModal(true);
        return;
      }
      
      // Claim the camp immediately when selected (free claim)
      if (!camp.claimed) {
        try {
          const { error: claimError } = await supabase
            .from('camps')
            .update({
              claimed: true,
              owner_id: session.user.id,
              claimed_at: new Date().toISOString()
              // Note: plan_id is set later when they pay for editing access
            })
            .eq('id', camp.id);
          
          if (claimError) {
            console.error('Error claiming camp:', claimError);
            toast.error('Failed to claim camp. Please try again.', { duration: 5000 });
            return;
          }
          
          console.log('Camp claimed successfully (free claim)');
          // Refresh camp data
          const { data: updatedCamp } = await supabase
            .from('camps')
            .select('id, name, slug, city, state, claimed, owner_id, plan_id')
            .eq('id', camp.id)
            .single();
          
          if (updatedCamp) {
            setSelectedCamp(updatedCamp);
            setShowSearch(false);
            sessionStorage.setItem('pending_claim_camp_slug', updatedCamp.slug);
            sessionStorage.setItem('pending_claim_camp_id', updatedCamp.id);
          }
        } catch (error) {
          console.error('Error claiming camp:', error);
          toast.error('Failed to claim camp. Please try again.', { duration: 5000 });
        }
      } else {
        // Camp already claimed, just set the state
        setSelectedCamp(camp);
        setShowSearch(false);
        sessionStorage.setItem('pending_claim_camp_slug', camp.slug);
        sessionStorage.setItem('pending_claim_camp_id', camp.id);
      }
    } else {
      // User not logged in - store for later
      setSelectedCamp(camp);
      setShowSearch(false);
      sessionStorage.setItem('pending_claim_camp_slug', camp.slug);
      sessionStorage.setItem('pending_claim_camp_id', camp.id);
    }
  };
  return (
    <div className="page-container">
      <SEO 
        title="Take Control of Your Camp Details | CampWhiz"
        description="Edit your camp listing to connect with local families and keep your camp information up to date."
      />
      
      <div className="page-content">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
              Take Control of Your Camp Details
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Edit your camp listing and connect with families in your area. 
              Keep your information accurate and get discovered by parents looking for the perfect camp.
            </p>
            
            {/* Camp Selection */}
            <div className="max-w-md mx-auto">
              {selectedCamp ? (
                /* Show selected camp with clear option */
                <div className="bg-white rounded-lg border-2 border-primary/20 p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <Link
                        to={`/camps/${selectedCamp.slug}`}
                        className="font-semibold text-gray-900 hover:text-primary transition-colors"
                        onClick={() => {
                          track('cta_clicked', {
                            persona: 'camp_owner',
                            page: 'mycamp_teaser',
                            cta_location: 'card_camp_name',
                            cta_text: 'Camp name link',
                            camp_id: selectedCamp?.id,
                            camp_slug: selectedCamp?.slug,
                            destination: 'camp_page',
                          });
                          trackEvent('mycamp_teaser_camp_name_clicked', {
                            camp_id: selectedCamp?.id,
                            camp_slug: selectedCamp?.slug,
                          });
                        }}
                      >
                        <h3>{selectedCamp.name}</h3>
                      </Link>
                      <p className="text-sm text-gray-600">{selectedCamp.city}, {selectedCamp.state}</p>
                    </div>
                    <button
                      onClick={() => {
                        track('cta_clicked', {
                          persona: 'camp_owner',
                          page: 'mycamp_teaser',
                          cta_location: 'card_clear_camp',
                          cta_text: 'Clear camp',
                          camp_id: selectedCamp?.id,
                          camp_slug: selectedCamp?.slug,
                        });
                        trackEvent('mycamp_teaser_camp_cleared', {
                          camp_id: selectedCamp?.id,
                          camp_slug: selectedCamp?.slug,
                        });
                        clearSelectedCamp();
                      }}
                      className="ml-3 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                      title="Clear selection and search for another camp"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  {selectedCamp.claimed && selectedCamp.owner_id === session?.user?.id && !selectedCamp.plan_id ? (
                    <div className="space-y-3">
                      <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200 mb-3">
                        <p className="text-sm text-gray-800 font-medium mb-2">
                          🎉 Camp Claimed Successfully!
                        </p>
                        <p className="text-xs text-gray-600 mb-3">
                          To edit your camp details, photos, and connect with families, upgrade to a plan.
                        </p>
                        <Link
                          to="/pricing/owners"
                          className="btn btn-primary w-full text-sm"
                          onClick={() => {
                            track('cta_clicked', {
                              persona: 'camp_owner',
                              page: 'mycamp_teaser',
                              cta_location: 'card_pricing_claimed_no_plan',
                              cta_text: 'View Pricing Plans',
                              camp_id: selectedCamp?.id,
                              camp_slug: selectedCamp?.slug,
                              destination: 'pricing_owners',
                            });
                            trackEvent('mycamp_teaser_cta_clicked', {
                              cta_location: 'card_pricing_claimed_no_plan',
                              camp_id: selectedCamp?.id,
                              destination: 'pricing_owners',
                            });
                          }}
                        >
                          View Pricing Plans
                        </Link>
                      </div>
                      <Link
                        to={`/camps/${selectedCamp.slug}`}
                        className="btn btn-secondary w-full"
                        onClick={() => {
                          track('cta_clicked', {
                            persona: 'camp_owner',
                            page: 'mycamp_teaser',
                            cta_location: 'card_view_camp_page',
                            cta_text: 'View Camp Page',
                            camp_id: selectedCamp?.id,
                            camp_slug: selectedCamp?.slug,
                            destination: 'camp_page',
                          });
                          trackEvent('mycamp_teaser_cta_clicked', {
                            cta_location: 'card_view_camp_page',
                            camp_id: selectedCamp?.id,
                            destination: 'camp_page',
                          });
                        }}
                      >
                        View Camp Page
                      </Link>
                    </div>
                  ) : selectedCamp.claimed && selectedCamp.owner_id === session?.user?.id && selectedCamp.plan_id ? (
                    <Link
                      to={`/camps/${selectedCamp.slug}`}
                      className="btn btn-primary w-full"
                      onClick={() => {
                        track('cta_clicked', {
                          persona: 'camp_owner',
                          page: 'mycamp_teaser',
                          cta_location: 'card_go_to_my_camp',
                          cta_text: 'Go to My Camp',
                          camp_id: selectedCamp?.id,
                          camp_slug: selectedCamp?.slug,
                          destination: 'camp_page',
                        });
                        trackEvent('mycamp_teaser_cta_clicked', {
                          cta_location: 'card_go_to_my_camp',
                          camp_id: selectedCamp?.id,
                          destination: 'camp_page',
                        });
                      }}
                    >
                      Go to My Camp
                    </Link>
                  ) : (
                    <Link
                      to="/pricing/owners"
                      className="btn btn-primary w-full"
                      onClick={() => {
                        track('cta_clicked', {
                          persona: 'camp_owner',
                          page: 'mycamp_teaser',
                          cta_location: 'card_view_pricing',
                          cta_text: 'View Pricing Plans',
                          camp_id: selectedCamp?.id,
                          camp_slug: selectedCamp?.slug,
                          destination: 'pricing_owners',
                        });
                        trackEvent('mycamp_teaser_cta_clicked', {
                          cta_location: 'card_view_pricing',
                          camp_id: selectedCamp?.id,
                          destination: 'pricing_owners',
                        });
                      }}
                    >
                      View Pricing Plans
                    </Link>
                  )}
                </div>
              ) : (
                /* Show search input */
                <CampSearchInput 
                  placeholder="Search by camp name to claim..."
                  className="w-full"
                  onCampSelect={handleCampSelect}
                />
              )}
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-xl p-3 shadow bg-purple-50">
                  <CheckCircleIcon className="h-7 w-7 text-primary" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Keep Info Updated
              </h3>
              <p className="text-gray-500 text-base">
                Update your camp details, schedules, and contact information anytime to ensure families have the most current information.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-xl p-3 shadow bg-purple-50">
                  <StarIcon className="h-7 w-7 text-primary" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Get Discovered
              </h3>
              <p className="text-gray-500 text-base">
                Appear in local searches and recommendations, helping parents in your area find the perfect camp for their children.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 text-center md:col-span-2 lg:col-span-1">
              <div className="flex justify-center mb-4">
                <div className="rounded-xl p-3 shadow bg-purple-50">
                  <BuildingStorefrontIcon className="h-7 w-7 text-primary" aria-hidden="true" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Professional Presence
              </h3>
              <p className="text-gray-500 text-base">
                Showcase your camp with photos, detailed descriptions, and verified contact information to build trust with families.
              </p>
            </div>
          </div>

          {/* Pricing Highlight */}
          <div className="bg-gradient-to-r from-primary to-purple-600 rounded-2xl p-8 text-white text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">
              🎉 Limited Time: Save 50% with Early Bird Pricing
            </h2>
            <p className="text-lg mb-6">
              Just $149 (normally $290)
            </p>
            <Link 
              to="/pricing/owners"
              className="inline-block bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              onClick={() => {
                track('cta_clicked', {
                  persona: 'camp_owner',
                  page: 'mycamp_teaser',
                  cta_location: 'banner_early_bird',
                  cta_text: 'View Pricing Plans',
                  destination: 'pricing_owners',
                });
                trackEvent('mycamp_teaser_cta_clicked', {
                  cta_location: 'banner_early_bird',
                  destination: 'pricing_owners',
                });
              }}
            >
              View Pricing Plans
            </Link>
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
              How It Works
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Find Your Camp
                </h3>
                <p className="text-gray-500 text-base">
                  Search for your camp in our directory or browse by location to find your listing.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Claim & Verify
                </h3>
                <p className="text-gray-500 text-base">
                  Click "Claim Your Listing" and verify your ownership to take control of your camp page.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Upgrade & Start Managing
                </h3>
                <p className="text-gray-500 text-base">
                  Choose a plan, upgrade, and start updating your information, adding photos, and connecting with local families.
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-4">
              Can&apos;t find your camp?{' '}
              <a
                href="mailto:hello@campwhiz.com?subject=Add%20a%20Camp%20to%20CampWhiz"
                className="text-primary hover:underline"
                onClick={() => {
                  track('cta_clicked', {
                    persona: 'camp_owner',
                    page: 'mycamp_teaser',
                    cta_location: 'footer_contact_us',
                    cta_text: 'Contact us',
                    destination: 'mailto',
                  });
                  trackEvent('mycamp_teaser_cta_clicked', {
                    cta_location: 'footer_contact_us',
                    destination: 'mailto',
                  });
                }}
              >
                Contact us
              </a>{' '}
              to add it to our directory.
            </p>
          </div>
        </div>
      </div>
      
      <AlreadyClaimedModal 
        isOpen={showAlreadyClaimedModal}
        onClose={() => setShowAlreadyClaimedModal(false)}
      />
    </div>
  );
}
